import { Sidebar, SidebarProvider, SidebarContent, SidebarGroup, SidebarGroupContent } from "@/modern/components/ui/sidebar";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import ManageView from "./tabs/ManageView";
import { ViewStateInspection } from "./tabs/ViewStateInspection";
import { TimescalesTab } from "./tabs/TimescalesTab";
import { SettingsTab } from "./tabs/SettingsTab";
import { Calendar, X, Layers, Save, Trash, Settings, SlidersHorizontal, Share2 } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getViewStateIconByRecord } from "@/other/utils/utils";
import { Button } from "@/modern/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { useViewEditor, ViewEditorProvider } from "./context/ViewEditorContext";
import { View } from "@/bryntum-lib/models/View";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { ShareView } from "./tabs/ShareView";


const TRIGGER_CLS = "w-full justify-start gap-2 px-3 py-2 h-auto text-sm font-medium text-foreground/90 hover:bg-primary/5 transition-all duration-150 rounded-lg data-[state=active]:text-foreground data-[state=active]:bg-primary/10 data-[state=active]:border-l-4 data-[state=active]:border-primary [&[data-state=active]_svg]:text-primary"

export const SwivuViewEditorContent = () => {
  const context = useSwivuBryntumContext();
  const { project } = context;
  const { onRemoveView, onSaveView, isSaveAsView, isDeleteDisabled, isSaveDisabled, record, onClose, editorInstance, canUseRecordViewState } = useViewEditor();
  const viewIcon = getViewStateIconByRecord(record, project);

  return (<BaseModal
    title="View Editor"
    description=""
    onClose={onClose}
    size={{
      minWidth: "min(78rem, 95vw)",
      minHeight: "min(43rem, 95vh)",
      width: "min(78rem, 95vw)",
      height: "min(43rem, 95vh)",
      maxWidth: "95vw",
      maxHeight: "95vh"
    }}
    editorInstance={editorInstance}
    record={record}
    headerIconCls=""
    modal={true}
    version=""
    hideMenu={true}
    headerRenderer={() => {
      return (
        <div className="drag-handle bg-muted/50 dark:bg-background/50 border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-foreground leading-none flex items-center gap-2" style={record?.color ? { color: record.color } : undefined}>
            <i className={`${viewIcon}`} style={record?.color ? { color: record.color } : undefined} />
            {record?.name}
          </h2>


          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRemoveView} disabled={isDeleteDisabled || !record}>
              Delete <Trash className="w-4 h-4" />
            </Button>
            <Button size="sm" onClick={onSaveView} disabled={isSaveDisabled || !record}>
              {isSaveAsView ? "Save View As" : "Save View"} <Save className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )
    }}
  >
    <Tabs defaultValue="view-details" className="flex-1 overflow-auto">
      <SidebarProvider className="items-start h-full">
        <Sidebar collapsible="none" className="hidden md:flex bg-muted/50 dark:bg-background/50 border-r border-border">
          <SidebarContent className="justify-between">
            <SidebarGroup>
              <SidebarGroupContent>
                <TabsList className="h-auto w-full flex-col bg-transparent space-y-1 p-0">
                  <TabsTrigger
                    value="view-details"
                    className={TRIGGER_CLS}
                    activeElementClassName="!hidden"
                  >
                    <Settings className="w-4 h-4 text-muted-foreground" />
                    General
                  </TabsTrigger>
                  <TabsTrigger
                    value="share-view"
                    className={TRIGGER_CLS}
                    activeElementClassName="!hidden"
                  >
                    <Share2 className="w-4 h-4 text-muted-foreground" />
                    Share View
                  </TabsTrigger>
                  <TabsTrigger
                    value="state-configurations"
                    className={TRIGGER_CLS}
                    activeElementClassName="!hidden"
                  >
                    <Layers className="w-4 h-4 text-muted-foreground" />
                    Inspect State
                  </TabsTrigger>
                  {record?.viewType === VIEW_TYPES.schedulerView && (
                    <>
                      <TabsTrigger
                        value="timescales"
                        className={TRIGGER_CLS}
                        activeElementClassName="!hidden"
                      >
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        Timescales
                      </TabsTrigger>
                      <TabsTrigger
                        value="schedule-settings"
                        className={TRIGGER_CLS}
                        activeElementClassName="!hidden"
                      >
                        <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
                        Schedule Settings
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
              </SidebarGroupContent>
            </SidebarGroup>

            <div className="flex flex-col gap-1 px-3 pb-2">
              <p className="text-sm text-muted-foreground m-0">
                Created by {record?.isDefaultView ? "Swivu" : record?.creatorUserName}
              </p>
              {!record?.isOwnView && (
                <p className="text-sm text-amber-600 dark:text-amber-500 m-0">
                  {record?.creatorUserName || "N/A"} is the only user that can save changes to this view
                </p>
              )}
            </div>
          </SidebarContent>
        </Sidebar>

        <div className="flex flex-1 flex-col h-full overflow-hidden bg-background">
          <div className="flex-1 overflow-y-auto">
            <TabsContent value="view-details" className="m-0 h-full">
              <ManageView />
            </TabsContent>

            <TabsContent value="state-configurations" className="m-0 h-full p-4">
              <ViewStateInspection
                record={record}
                context={context}
                canUseRecordViewState={canUseRecordViewState}
              />
            </TabsContent>

            <TabsContent value="timescales" className="m-0 h-full">
              <TimescalesTab />
            </TabsContent>

            <TabsContent value="schedule-settings" className="m-0 h-full p-4">
              <SettingsTab />
            </TabsContent>

            <TabsContent value="share-view" className="m-0 h-full p-4">
              <ShareView />
            </TabsContent>
          </div>
        </div>
      </SidebarProvider>
    </Tabs>
  </BaseModal>)
}

export interface ViewEditorProps {
  record: {
    viewRecord?: View;
    canUseRecordViewState?: boolean;
    isSaveAsView?: boolean;
  };
  onClose: () => void;
  editorInstance: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export const ViewEditor = (props: ViewEditorProps) => {
  return (
    <ViewEditorProvider {...props}>
      <SwivuViewEditorContent />
    </ViewEditorProvider>
  );
};

export default ViewEditor;